import { Link } from "@tanstack/react-router"
import { ChevronRight } from "lucide-react"
import type * as React from "react"
import { useDocumentTitle } from "@/hooks/use-document-title"
import { cn } from "@/lib/utils"

export interface Crumb {
  label: string
  to?: string
}

/**
 * Width-constrained, padded page body. Use `width="full"` for tables and
 * dashboards that benefit from the whole canvas.
 */
export function PageContainer({
  width = "default",
  className,
  ...props
}: React.ComponentProps<"div"> & { width?: "narrow" | "default" | "wide" | "full" }) {
  return (
    <div
      data-slot="page-container"
      className={cn(
        "mx-auto w-full px-4 pt-6 pb-16 sm:px-6 lg:px-8 lg:pt-8",
        width === "narrow" && "max-w-3xl",
        width === "default" && "max-w-5xl",
        width === "wide" && "max-w-7xl",
        className,
      )}
      {...props}
    />
  )
}

export interface PageHeaderProps {
  title: React.ReactNode
  /** Plain-text title used for `document.title` when `title` is not a string. */
  documentTitle?: string
  description?: React.ReactNode
  /** Primary/secondary actions, right-aligned on desktop. */
  actions?: React.ReactNode
  /** Optional in-page trail for nested pages (the top bar already shows the section). */
  breadcrumbs?: Crumb[]
  icon?: React.ComponentType<{ className?: string }>
  /** Small element rendered next to the title (e.g. a status badge). */
  badge?: React.ReactNode
  className?: string
  children?: React.ReactNode
}

export function PageHeader({
  title,
  documentTitle,
  description,
  actions,
  breadcrumbs,
  icon: Icon,
  badge,
  className,
  children,
}: PageHeaderProps) {
  useDocumentTitle(documentTitle ?? (typeof title === "string" ? title : undefined))
  return (
    <header data-slot="page-header" className={cn("mb-6 flex flex-col gap-4 lg:mb-8", className)}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="text-muted-foreground flex flex-wrap items-center gap-1 text-xs"
        >
          {breadcrumbs.map((c, i) => (
            <span key={`${c.label}-${i}`} className="inline-flex items-center gap-1">
              {i > 0 && <ChevronRight className="size-3 opacity-60" aria-hidden />}
              {c.to ? (
                <Link to={c.to} className="hover:text-foreground transition-colors">
                  {c.label}
                </Link>
              ) : (
                <span className="text-foreground">{c.label}</span>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && (
            <div className="bg-muted text-muted-foreground mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg ring-1 ring-foreground/5">
              <Icon className="size-4.5" />
            </div>
          )}
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl font-semibold tracking-tight text-balance">{title}</h1>
              {badge}
            </div>
            {description && (
              <p className="text-muted-foreground max-w-2xl text-sm text-pretty">{description}</p>
            )}
          </div>
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children}
    </header>
  )
}

/**
 * A titled block inside a page. `layout="split"` puts the heading on the left
 * and the content on the right (settings-style) on wide screens.
 */
export function PageSection({
  title,
  description,
  actions,
  layout = "stacked",
  className,
  contentClassName,
  children,
  id,
}: {
  title?: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
  layout?: "stacked" | "split"
  className?: string
  contentClassName?: string
  children?: React.ReactNode
  id?: string
}) {
  const heading = (title || description || actions) && (
    <div
      className={cn(
        "flex items-start justify-between gap-4",
        layout === "split" && "lg:flex-col lg:justify-start",
      )}
    >
      <div className="space-y-1">
        {title && <h2 className="text-sm font-semibold">{title}</h2>}
        {description && <p className="text-muted-foreground text-sm text-pretty">{description}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  )
  return (
    <section
      id={id}
      data-slot="page-section"
      className={cn(
        "scroll-mt-20",
        layout === "split"
          ? "grid gap-4 lg:grid-cols-[minmax(0,15rem)_minmax(0,1fr)] lg:gap-10"
          : "space-y-4",
        className,
      )}
    >
      {heading}
      <div className={cn("min-w-0", contentClassName)}>{children}</div>
    </section>
  )
}

/** Bordered surface for grouping form fields or content blocks. */
export function Panel({
  className,
  footer,
  children,
  tone = "default",
  ...props
}: React.ComponentProps<"div"> & { footer?: React.ReactNode; tone?: "default" | "danger" }) {
  return (
    <div
      data-slot="panel"
      className={cn(
        "bg-card overflow-hidden rounded-xl border",
        tone === "danger" && "border-destructive/30",
        className,
      )}
      {...props}
    >
      <div className="p-4 sm:p-5">{children}</div>
      {footer && (
        <div
          className={cn(
            "bg-muted/40 flex flex-wrap items-center justify-end gap-2 border-t px-4 py-3 sm:px-5",
            tone === "danger" && "border-destructive/20 bg-destructive/5",
          )}
        >
          {footer}
        </div>
      )}
    </div>
  )
}
